/**
 * Offline Queue Manager
 * Handles queuing of updates when WebSocket is disconnected.
 * Extracted from websocket.js for better separation of concerns.
 *
 * Updates are queued with their raw data and encrypted on flush
 * to ensure the current encryption key is used.
 */

/**
 * Create an offline queue manager
 * @param {Object} options
 * @param {number} options.maxSize - Maximum queue size before dropping oldest (default 500)
 * @param {Function} options.log - Callback for logging (optional)
 * @returns {Object} Offline queue manager API
 */
export function createOfflineQueueManager(options = {}) {
  const {
    maxSize = 500,
    log = () => {},
    onDropped = null,
  } = options;

  // Internal state
  let queue = [];

  /**
   * Add an item to the queue
   * Enforces max size by dropping oldest items (FIFO)
   * @param {Object} item - The item to queue (typically { updateInfo, queuedAt })
   */
  function enqueue(item) {
    queue.push(item);

    // Enforce queue size limit — signal when compaction is needed.
    // The caller (SyncManager) should compact by replacing all queued items
    // with a single fullState snapshot from the current Y.Doc.
    if (queue.length > maxSize) {
      if (onDropped) {
        onDropped(queue.length);
      }
    }
  }

  /**
   * Flush all queued items
   * Calls the provided callback for each item, removing only successfully processed items.
   * Failed items remain in the queue for retry on next flush.
   * @param {Function} callback - Async callback to process each item
   * @returns {Promise<number>} Number of items flushed
   */
  async function flush(callback) {
    if (queue.length === 0) {
      return 0;
    }

    log(`Flushing ${queue.length} queued updates`);

    let processedCount = 0;
    const failedItems = [];

    // Process items in order, collecting failures for re-queue
    while (queue.length > 0) {
      const item = queue.shift();
      try {
        await callback(item);
        processedCount++;
      } catch (err) {
        log(`Failed to flush queued update: ${err.message || err}`);
        failedItems.push(item);
        // Stop on first failure — remaining items likely depend on ordering
        break;
      }
    }

    // Re-queue failed item + remaining unprocessed items (preserving order)
    if (failedItems.length > 0 || queue.length > 0) {
      queue = [...failedItems, ...queue];
      log(`${queue.length} items remain in queue for retry`);
    }

    return processedCount;
  }

  /**
   * Clear the queue without processing
   */
  function clear() {
    const count = queue.length;
    queue = [];
    if (count > 0) {
      log(`Cleared ${count} queued updates`);
    }
  }

  /**
   * Get current queue size
   * @returns {number}
   */
  function size() {
    return queue.length;
  }

  /**
   * Check if queue is empty
   * @returns {boolean}
   */
  function isEmpty() {
    return queue.length === 0;
  }

  /**
   * Get a copy of the current queue (for debugging/inspection)
   * @returns {Array}
   */
  function getQueue() {
    return [...queue];
  }

  return {
    enqueue,
    flush,
    clear,
    size,
    isEmpty,
    getQueue,
  };
}
